<app-report-header
  crumbLabel="Predictive FX Calculator"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-header>

<div class="rpt-page" id="contentToConvert">

  <!-- ── Masthead ── -->
  <div class="rpt-wrap rpt-masthead">
    <div class="rpt-masthead__eyebrow">Predictive Foreign Exchange Report</div>
    <h1 class="rpt-masthead__title">FX Projection — £{{calcData?.InvestmentGBP | number:'1.0-0'}} over {{calcData?.investedTenure}} Years</h1>
    <p class="rpt-masthead__subtitle">
      Projected investment value in {{calcData?.homecurrencyText}} as the exchange rate appreciates at {{calcData?.fxgrowth}}% per annum over the holding period.
    </p>
    <div class="rpt-masthead__meta">
      <span class="rpt-meta-pill">Investment <strong>£{{calcData?.InvestmentGBP | number:'1.0-0'}}</strong></span>
      <span class="rpt-meta-pill">FX Growth <strong>{{calcData?.fxgrowth}}% p.a.</strong></span>
      <span class="rpt-meta-pill">Period <strong>{{calcData?.investedTenure}} years</strong></span>
      <span class="rpt-meta-pill rpt-meta-pill--accent">Exit Value <strong>{{formatHomeCurrencyFull(exitValueHome)}}</strong></span>
    </div>
  </div>

  <!-- ── §01 KPI Section ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">01 · Results</div>
        <h2 class="rpt-section__title">Projected FX returns</h2>
        <p class="rpt-section__sub">Exit value and FX-driven growth in {{calcData?.homecurrencyText}} at the end of the holding period.</p>
      </div>
      <span class="rpt-section__tag">{{calcData?.investedTenure}}-year horizon · {{calcData?.homecurrencyText}} · {{calcData?.homecurrency | number:'1.0-4'}} / £</span>
    </div>

    <div class="rpt-kpi-strip">
      <!-- Hero: Exit Value -->
      <div class="rpt-kpi-hero">
        <div class="rpt-kpi-hero__eyebrow">Projected Exit Value · {{calcData?.homecurrencyText}}</div>
        <div class="rpt-kpi-hero__body">
          <div class="rpt-kpi-hero__value">{{formatHomeCurrencyFull(exitValueHome)}}</div>
          <div class="rpt-kpi-hero__label">Projected exit value after {{calcData?.investedTenure}} years</div>
        </div>
        <p class="rpt-kpi-hero__caption">
          Starting from £{{calcData?.InvestmentGBP | number:'1.0-0'}} with FX appreciating at {{calcData?.fxgrowth}}% p.a. over {{calcData?.investedTenure}} years.
        </p>
      </div>

      <!-- Side: Initial Value + FX Growth -->
      <div class="rpt-kpi-side rpt-kpi-side--2">
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Initial Value ({{calcData?.homecurrencyText}})</div>
          <div class="rpt-kpi-card__value">{{formatHomeCurrencyFull(initialValueHome)}}</div>
          <div class="rpt-kpi-card__foot">Current value in home currency</div>
        </div>
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">FX-Only Growth</div>
          <div class="rpt-kpi-card__value">{{fxOnlyGrowthPct | number:'1.1-1'}}<sup>%</sup></div>
          <div class="rpt-kpi-card__foot">Over {{calcData?.investedTenure}} years</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §02 Assumptions ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">02 · Inputs</div>
        <h2 class="rpt-section__title">Your assumptions</h2>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-card__body">
        <div class="rpt-arow">
          <span class="rpt-arow__key">Investment (GBP)</span>
          <span class="rpt-arow__val">£{{calcData?.InvestmentGBP | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Home Currency</span>
          <span class="rpt-arow__val">{{calcData?.homecurrencyText}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Current FX Rate</span>
          <span class="rpt-arow__val">{{calcData?.homecurrencyText}} @ {{calcData?.homecurrency | number:'1.0-4'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">FX Growth Rate</span>
          <span class="rpt-arow__val">{{calcData?.fxgrowth}}% p.a.</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Investment Period</span>
          <span class="rpt-arow__val">{{calcData?.investedTenure}} years</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §03 Chart ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">03 · Trajectory</div>
        <h2 class="rpt-section__title">FX value over time</h2>
        <p class="rpt-section__sub">How your investment value grows in {{calcData?.homecurrencyText}} as the exchange rate appreciates.</p>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-chart-wrap pfx-chart-wrap">
        <canvas id="pfxCanvas"></canvas>
      </div>
    </div>
  </section>

  <!-- ── §04 Year-by-Year Table ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">04 · Year-by-Year</div>
        <h2 class="rpt-section__title">Annual projection</h2>
        <p class="rpt-section__sub">FX rate, home currency value, and cumulative growth for each year.</p>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-tbl-wrap">
        <table class="rpt-tbl">
          <thead>
            <tr>
              <th>Year</th>
              <th>FX Rate</th>
              <th>{{calcData?.homecurrencyText}} Value</th>
              <th style="text-align:right">Growth %</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of yearlyRows">
              <td style="font-weight:600; color: var(--navy)">{{row.year}}</td>
              <td>{{row.rate | number:'1.0-4'}}</td>
              <td>{{formatHomeCurrency(row.value)}}</td>
              <td class="rpt-tbl__accent">+{{row.growthPct | number:'1.1-1'}}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>

</div>

<app-report-footer
  [today]="today"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-footer>
